import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, KPIDeleteConfirmModal, MultiSelectDropdown, DepartmentPicker } from '../../components';
import { useTargetDepartment } from '../../hooks/useTargetDepartment';
import CancelConfirmModal from '../../components/CancelConfirmModal';
import { useCancelConfirm } from '../../hooks/useCancelConfirm';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { servicesApi, goalsApi, organizationalUnitsApi, kpisApi } from '../../services/apiService';
import { StrategicGoalList, OrganizationalUnitList, KPIList } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { unitTreeOptions } from '../../utils/unitTree';
import { useRoleNavigation, useUnsavedChangesWarning } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { extractKpiErrorMessage, validateKpiRequiredFields, validateKpiTargetConfigs } from '../../utils/kpiErrors';
import { normalizeArabicNumbers } from '../../utils/rtl';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import KpiAnnualTargetSection, {
  KpiTargetConfig,
  emptyKpiTargetConfig,
  configFromKpi,
  buildKpiTargetPayload,
} from '../kpis/components/KpiAnnualTargetSection';
type RouteProps = RouteProp<MainStackParamList, 'AddService'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronDownIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronUpIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 15L12 9L6 15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrashIcon = ({ size = 16, color = tokens.colors.raw.red["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 6H5H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const DragIcon = ({ size = 16, color = tokens.colors.raw.gray["400"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 6H8.01M8 12H8.01M8 18H8.01M16 6H16.01M16 12H16.01M16 18H16.01" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 20, color = tokens.colors.raw.slate["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 2V6M8 2V6M3 10H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const Dropdown = ({ value, options, placeholder, onChange, disabled }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const buttonRef = useRef<View>(null);
  const menuId = useRef(`dropdown-menu-${Math.random().toString(36).slice(2)}`).current;
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: disabled ? (isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"]) : colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    icon: colors.textMuted,
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBorder: isDark ? colors.border : tokens.colors.raw.slate["100"],
  };

  const handleOpenDropdown = () => {
    if (!isOpen && buttonRef.current) {
      buttonRef.current.measureInWindow((x, y, width, height) => {
        setPosition({
          top: y + height + 4,
          left: x,
          width,
        });
      });
    }
    setIsOpen(!isOpen);
  };

  const handleCloseDropdown = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (buttonRef.current && !target.closest(`[data-dropdown-id="${menuId}"]`)) {
        handleCloseDropdown();
      }
    };

    const handleScroll = (e: Event) => {
      // Don't close if scrolling inside the dropdown menu itself
      const target = e.target as HTMLElement;
      if (target?.closest?.(`[data-dropdown-id="${menuId}"]`)) return;
      handleCloseDropdown();
    };

    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const dropdownMenu = isOpen && position ? (
    <div
      data-dropdown-id={menuId}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        insetInlineStart: `${position.left}px`,
        width: `${position.width}px`,
        backgroundColor: dropdownTheme.menuBg,
        borderColor: dropdownTheme.menuBorder,
        borderWidth: tokens.spacing.absolute["px"],
        borderRadius: tokens.radii.absolute["radius-8"],
        marginTop: tokens.spacing.absolute["1"],
        zIndex: 1000,
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        maxHeight: 250,
        overflowY: 'auto',
      }}
    >
      {options.length === 0 && (
        <View style={[styles.dropdownItem, { borderBottomColor: dropdownTheme.itemBorder }]}>
          <Text style={[styles.dropdownItemText, { color: dropdownTheme.placeholder }]}>{t('common.noData')}</Text>
        </View>
      )}
      {options.map((option) => (
        <TouchableOpacity
          key={option.value}
          style={[styles.dropdownItem, { borderBottomColor: dropdownTheme.itemBorder }]}
          onPress={() => {
            onChange(option.value);
            handleCloseDropdown();
          }}
        >
          <Text style={[styles.dropdownItemText, { color: dropdownTheme.text }]}>{option.label}</Text>
        </TouchableOpacity>
      ))}
    </div>
  ) : null;

  return (
    <>
      <View style={styles.dropdownContainer}>
        <TouchableOpacity
          ref={buttonRef}
          style={[styles.dropdownButton, { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border }]}
          onPress={handleOpenDropdown}
          disabled={disabled}
        >
          <Text style={[
            styles.dropdownButtonText,
            { color: dropdownTheme.text },
            !selectedOption && { color: dropdownTheme.placeholder }
          ]}>
            {selectedOption?.label || placeholder}
          </Text>
          <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        </TouchableOpacity>
      </View>
      {dropdownMenu && ReactDOM.createPortal(dropdownMenu, document.body)}
    </>
  );
};

// ============================================================
// KPI Card Component (for Performance Indicators)
// ============================================================

interface KPIFormData {
  id: number;
  name: string;
  weight: string;
  description: string;
  kpiType: string;
  polarity: string;
  startDate: string;
  unit: string;
  frequency: string;
  formula: string;
  targetValue: string;
  worstAcceptableValue: string;
  dataSource: string;
  target: KpiTargetConfig;
  isExpanded: boolean;
  isExisting?: boolean;  // Flag to identify existing KPIs from API
  apiId?: number;        // Original API ID for existing KPIs
}

interface KPICardProps {
  kpi: KPIFormData;
  index: number;
  onUpdate: (id: number, data: Partial<KPIFormData>) => void;
  onDelete: (id: number) => void;
  onToggleExpand: (id: number) => void;
}

const KPICard = ({ kpi, index, onUpdate, onDelete, onToggleExpand }: KPICardProps) => {
  const { colors } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const kpiTypeOptions: DropdownOption[] = [
    { value: 'strategic', label: t('kpis.strategic') },
    { value: 'operational', label: t('kpis.operational') },
    { value: 'efficiency', label: t('kpis.efficiency') },
    { value: 'effectiveness', label: t('kpis.effectiveness') },
    { value: 'impact', label: t('kpis.impact') },
  ];

  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: t('kpis.ascending') },
    { value: 'negative', label: t('kpis.descending') },
  ];

  const unitOptions: DropdownOption[] = [
    { value: '%', label: t('kpis.percentage') },
    { value: 'number', label: t('kpis.number') },
    { value: 'currency', label: t('kpis.currency') },
  ];

  const frequencyOptions: DropdownOption[] = [
    { value: 'daily', label: t('kpis.daily') },
    { value: 'weekly', label: t('kpis.weekly') },
    { value: 'monthly', label: t('kpis.monthly') },
    { value: 'quarterly', label: t('kpis.quarterly') },
    { value: 'half_yearly', label: t('kpis.semiAnnual') },
    { value: 'yearly', label: t('kpis.annual') },
  ];

  return (
    <View style={[styles.kpiCard, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}>
      {/* KPI Card Header */}
      <View style={styles.kpiCardHeader}>
        <TouchableOpacity style={styles.kpiHeaderButton} onPress={() => onToggleExpand(kpi.id)}>
          {kpi.isExpanded ? (
            <ChevronUpIcon size={20} color={tokens.colors.raw.slate["500"]} />
          ) : (
            <ChevronDownIcon size={20} color={tokens.colors.raw.slate["500"]} />
          )}
        </TouchableOpacity>
        <TouchableOpacity style={styles.kpiHeaderButton} onPress={() => onDelete(kpi.id)}>
          <TrashIcon size={20} color={tokens.colors.raw.red["500"]} />
        </TouchableOpacity>
        <View style={styles.kpiCardHeaderRight}>
          <Text style={styles.kpiCardTitle}>
            {kpi.name || t('kpis.kpiName')}
          </Text>
          <View style={styles.kpiIndexBadge}>
            <Text style={styles.kpiIndexText}>{index + 1}</Text>
          </View>
          <TouchableOpacity style={styles.kpiHeaderButton}>
            <DragIcon size={20} color={tokens.colors.raw.gray["400"]} />
          </TouchableOpacity>
        </View>
      </View>

      {/* KPI Card Content (Expandable) */}
      {kpi.isExpanded && (
        <View style={styles.kpiCardContent}>
          {/* Basic Information Section */}
          <View style={styles.kpiSubsection}>
            <Text style={styles.kpiSectionTitle}>{t('common.basicInfo')}</Text>

            {/* Name & Weight Row */}
            <View style={styles.row}>
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('common.weight')}</Text>
                </View>
                <View style={styles.weightInputRow}>
                  <TouchableOpacity style={styles.setWeightButton}>
                    <Text style={styles.setWeightButtonText}>{t('common.assignWeight')}</Text>
                  </TouchableOpacity>
                  <View style={styles.weightInputWrapper}>
                    <Text style={styles.percentSymbolInside}>%</Text>
                    <TextInput
                      style={styles.weightInput}
                      value={kpi.weight}
                      onChangeText={(v) => onUpdate(kpi.id, { weight: normalizeArabicNumbers(v) })}
                      keyboardType="numeric"
                      textAlign="right"
                    />
                  </View>
                </View>
              </View>
              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.kpiName')}</Text>
                </View>
                <TextInput
                  style={styles.input}
                  value={kpi.name}
                  onChangeText={(v) => onUpdate(kpi.id, { name: v })}
                  textAlign="right"
                />
              </View>
            </View>

            {/* Description */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={styles.label}>{t('kpis.kpiDescription')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={kpi.description}
                onChangeText={(v) => onUpdate(kpi.id, { description: v })}
                textAlign="right"
                multiline
                numberOfLines={2}
              />
            </View>
          </View>

          {/* Classification Section */}
          <View style={[styles.kpiSubsection, { zIndex: 30 }]}>
            <Text style={styles.kpiSectionTitle}>{t('common.classification')}</Text>
            <View style={[styles.row, { zIndex: 10 }]}>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.polarity')}</Text>
                </View>
                <Dropdown
                  value={kpi.polarity}
                  options={polarityOptions}
                  placeholder={t('kpis.selectPolarityHint')}
                  onChange={(v) => onUpdate(kpi.id, { polarity: v })}
                />
              </View>
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={styles.label}>{t('kpis.kpiClassification')}</Text>
                </View>
                <Dropdown
                  value={kpi.kpiType}
                  options={kpiTypeOptions}
                  placeholder={t('kpis.selectClassification')}
                  onChange={(v) => onUpdate(kpi.id, { kpiType: v })}
                />
              </View>
            </View>
          </View>

          {/* Measurement Section — shared section for both existing and new KPIs */}
          <KpiAnnualTargetSection
            config={kpi.target}
            onChange={(patch) => onUpdate(kpi.id, { target: { ...kpi.target, ...patch } })}
            unit={kpi.unit}
            frequency={kpi.frequency}
            startDate={kpi.startDate}
            formula={kpi.formula}
            dataSource={kpi.dataSource}
            onMeasurementChange={(field, value) => onUpdate(kpi.id, { [field]: value })}
          />
        </View>
      )}
    </View>
  );
};

// ============================================================
// Main Component
// ============================================================

export default function AddServiceScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const goalId = route.params?.goalId;
  const serviceId = route.params?.serviceId;
  const { navigateToHome, isViewOnly } = useRoleNavigation();
  const scrollToKpi = route.params?.scrollToKpi;

  // Redirect view-only users (executive) - they cannot add/edit
  useEffect(() => {
    if (isViewOnly) {
      console.log('🚫 AddService: View-only user - redirecting');
      navigation.goBack();
    }
  }, [isViewOnly, navigation]);
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();
  // Department this service is saved under. Single-department users always get
  // their own; a multi-department user must choose (create) or it follows the
  // service being edited.
  const { isMultiDepartment, departments, departmentId, setDepartmentId } = useTargetDepartment();
  // Drop the answer of a superseded goals/units request (quick department switches).
  const goalsRequestRef = useRef(0);
  const unitsRequestRef = useRef(0);
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const styles = useMemo(() => getStyles(isRTL), [isRTL]);
  const { translate, translateUnit } = useAutoTranslate();

  // Theme colors for form elements
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["50"],
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? tokens.colors.raw.gray["500"] : tokens.colors.raw.gray["400"],
    iconColor: colors.textMuted,
    kpiCard: colors.card,
    kpiCardBorder: colors.cardBorder,
    kpiCardHeader: colors.card,
    kpiNumber: isDark ? colors.backgroundAlt : tokens.colors.raw.slate["100"],
  };

  // Refs for scrolling
  const scrollViewRef = useRef<ScrollView>(null);
  const kpiSectionRef = useRef<View>(null);
  const kpiSectionY = useRef<number>(0);

  // Edit mode
  const isEditMode = !!serviceId;
  // Goals are loaded per department, so until a multi-department user picks
  // one there is nothing to offer: say so instead of an empty list. Units are
  // the whole organization and don't wait.
  const awaitingDepartment = !isEditMode && isMultiDepartment && !departmentId;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    strategicGoal: goalId?.toString() || '',
    owningUnits: [] as string[],
    serviceFees: '',
    requirementsConditions: '',
    stepsToObtain: '',
    deliveryChannels: '',
    targetCustomers: '',
    serviceLimitations: '',
    serviceHours: '',
    additionalInfo: '',
  });

  // KPIs state
  const [kpis, setKpis] = useState<KPIFormData[]>([]);
  const [deletedKpiIds, setDeletedKpiIds] = useState<number[]>([]);
  const [kpiPendingDelete, setKpiPendingDelete] = useState<{ localId: number; backendId: number | null; name: string } | null>(null);
  const [nextKpiId, setNextKpiId] = useState(1);

  // Existing KPIs linked to this service (for edit mode)
  const [existingKpis, setExistingKpis] = useState<KPIList[]>([]);
  const [existingKpisLoading, setExistingKpisLoading] = useState(false);

  // Dropdown options
  const [goals, setGoals] = useState<DropdownOption[]>([]);
  const [units, setUnits] = useState<DropdownOption[]>([]);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [unitsLoading, setUnitsLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useUnsavedChangesWarning(!!formData.name || !!formData.description);

  // Fetch goals and units. A multi-department user only ever gets the target
  // department's items (none until one is chosen), and when a new service's
  // department changes, a selection that is not in the new department is
  // dropped. A single-department user gets the same unfiltered request as before.
  const fetchGoals = useCallback(async () => {
    const requestId = ++goalsRequestRef.current;
    if (isMultiDepartment && !departmentId) {
      setGoals([]);
      setGoalsLoading(false);
      return;
    }
    try {
      setGoalsLoading(true);
      const data = await goalsApi.list({
        page_size: 100,
        ...(isMultiDepartment ? { department_id: departmentId } : {}),
      });
      if (requestId !== goalsRequestRef.current) return;
      const options = data.results.map((goal: StrategicGoalList) => ({
        value: goal.id.toString(),
        label: translate(goal.title),
      }));
      setGoals(options);
      if (isMultiDepartment && !isEditMode) {
        setFormData(prev => (
          !prev.strategicGoal || options.some(o => o.value === prev.strategicGoal)
            ? prev
            : { ...prev, strategicGoal: '' }
        ));
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
      if (isMultiDepartment && requestId === goalsRequestRef.current) {
        // Never keep another department's goals on screen (or selected, when creating).
        setGoals([]);
        if (!isEditMode) setFormData(prev => ({ ...prev, strategicGoal: '' }));
      }
    } finally {
      if (requestId === goalsRequestRef.current) setGoalsLoading(false);
    }
  }, [translate, isMultiDepartment, departmentId, isEditMode]);

  // "مسؤولية التنفيذ": every department and section in the organization, each
  // followed by its sub-units, whichever department the service is for.
  const fetchUnits = useCallback(async () => {
    const requestId = ++unitsRequestRef.current;
    try {
      setUnitsLoading(true);
      const data = await organizationalUnitsApi.list({ page_size: 9999 });
      if (requestId !== unitsRequestRef.current) return;
      setUnits(unitTreeOptions(data.results || [], (unit: OrganizationalUnitList) => translateUnit(unit.name_ar || unit.name)));
    } catch (error) {
      console.error('Error fetching units:', error);
    } finally {
      if (requestId === unitsRequestRef.current) setUnitsLoading(false);
    }
  }, [translateUnit]);

  // Fetch existing service data in edit mode
  const fetchService = useCallback(async () => {
    if (!serviceId) return;
    try {
      setInitialLoading(true);
      const serviceData = await servicesApi.get(serviceId);
      // An existing service stays in its department: the goals/units lists follow it.
      if (isMultiDepartment && serviceData.department_id) {
        setDepartmentId(serviceData.department_id);
      }
      setFormData({
        name: serviceData.name || '',
        description: serviceData.description || '',
        strategicGoal: serviceData.strategic_goal?.toString() || '',
        owningUnits: (serviceData.owning_units || []).map(String),
        serviceFees: serviceData.service_fees || '',
        requirementsConditions: serviceData.requirements_conditions || '',
        stepsToObtain: serviceData.steps_to_obtain || '',
        deliveryChannels: serviceData.delivery_channels || '',
        targetCustomers: serviceData.target_customers || '',
        serviceLimitations: serviceData.service_limitations || '',
        serviceHours: serviceData.service_hours || '',
        additionalInfo: serviceData.additional_info || '',
      });
    } catch (error) {
      console.error('Error fetching service:', error);
      showError(t('services.loadFailed'));
    } finally {
      setInitialLoading(false);
    }
  }, [serviceId, showError, isMultiDepartment, setDepartmentId]);

  // Fetch existing KPIs linked to this service and convert to form data
  const fetchExistingKpis = useCallback(async () => {
    if (!serviceId) return;
    try {
      setExistingKpisLoading(true);
      const data = await kpisApi.list({ source_service: serviceId, page_size: 100 });

      // Convert API KPIs to KPIFormData format
      const existingKpiData: KPIFormData[] = data.results.map((kpi: any, index: number) => ({
        id: -(index + 1), // Use negative IDs for existing KPIs to avoid conflicts
        name: kpi.name || '',
        weight: kpi.weight ? String(parseFloat(kpi.weight)) : '30',
        description: kpi.description || '',
        kpiType: kpi.kpi_type || 'operational',
        polarity: kpi.polarity || 'positive',
        startDate: kpi.measurement_start_date || '',
        unit: kpi.unit || '',
        frequency: kpi.frequency || 'monthly',
        formula: kpi.measurement_formula || '',
        targetValue: kpi.target_value || '0',
        worstAcceptableValue: kpi.worst_acceptable_value != null ? String(kpi.worst_acceptable_value) : '',
        dataSource: kpi.data_source || '',
        target: configFromKpi(kpi),
        isExpanded: false,
        isExisting: true,
        apiId: kpi.id,
      }));

      setKpis(existingKpiData);
      setNextKpiId(existingKpiData.length + 1);
      setExistingKpis(data.results); // Keep for reference if needed
    } catch (error) {
      console.error('Error fetching service KPIs:', error);
    } finally {
      setExistingKpisLoading(false);
    }
  }, [serviceId]);

  // Scroll to KPI section
  const scrollToKpiSection = useCallback(() => {
    if (scrollToKpi && kpiSectionY.current > 0) {
      setTimeout(() => {
        scrollViewRef.current?.scrollTo({ y: kpiSectionY.current - 100, animated: true });
        // Auto-add a new KPI form if none exist
        if (kpis.length === 0) {
          addKPI();
        }
      }, 300);
    }
  }, [scrollToKpi, kpis.length]);

  // The lookup lists reload when the target department changes; the service
  // itself is loaded by the separate effect below so that does not refetch it.
  useEffect(() => {
    fetchGoals();
    fetchUnits();
  }, [fetchGoals, fetchUnits]);

  useEffect(() => {
    if (isEditMode) {
      fetchService();
      fetchExistingKpis();
    }
  }, [isEditMode, fetchService, fetchExistingKpis, translate, translateUnit]);

  // Scroll to KPI section after loading
  useEffect(() => {
    if (!initialLoading && scrollToKpi) {
      scrollToKpiSection();
    }
  }, [initialLoading, scrollToKpi, scrollToKpiSection]);

  // KPI handlers
  const addKPI = () => {
    const newKpi: KPIFormData = {
      id: nextKpiId,
      name: '',
      weight: '30',
      description: '',
      kpiType: '',
      polarity: '',
      startDate: '',
      unit: '%',
      frequency: 'yearly',
      formula: '',
      targetValue: '0.0',
      worstAcceptableValue: '',
      dataSource: '',
      target: emptyKpiTargetConfig,
      isExpanded: true,
      isExisting: false, // new KPI: validator must read the grid, not the scalar targetValue
    };
    setKpis([...kpis, newKpi]);
    setNextKpiId(nextKpiId + 1);
  };

  const updateKPI = (id: number, data: Partial<KPIFormData>) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, ...data } : kpi));
  };

  const deleteKPI = (id: number) => {
    const kpiToRemove = kpis.find(kpi => kpi.id === id);
    const backendId = kpiToRemove?.isExisting && kpiToRemove.apiId ? kpiToRemove.apiId : null;
    setKpiPendingDelete({
      localId: id,
      backendId,
      name: (kpiToRemove as any)?.name || '',
    });
  };

  const confirmDeleteKPI = () => {
    if (!kpiPendingDelete) return;
    const { localId, backendId } = kpiPendingDelete;
    if (backendId !== null) {
      setDeletedKpiIds(prev => [...prev, backendId]);
    }
    setKpis(prev => prev.filter(kpi => kpi.id !== localId));
    setKpiPendingDelete(null);
  };

  const toggleKPIExpand = (id: number) => {
    setKpis(kpis.map(kpi => kpi.id === id ? { ...kpi, isExpanded: !kpi.isExpanded } : kpi));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('services.serviceName') + ' ' + t('common.required');
    }
    if (!formData.strategicGoal) {
      newErrors.strategicGoal = t('goals.strategicGoal') + ' ' + t('common.required');
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return false;

    const kpiError = validateKpiRequiredFields(kpis, t);
    if (kpiError) { showError(kpiError); return false; }
    // Inline target-config validation (end date, pattern/step values) — catch an
    // invalid setup here instead of letting the server 400 it on save.
    const kpiTargetError = validateKpiTargetConfigs(kpis, t);
    if (kpiTargetError) { showError(kpiTargetError); return false; }
    return true;
  };

  const handleSave = async () => {
    // The goal cannot be picked before the department is, so ask for it first.
    if (!isEditMode && departments.length > 1 && !departmentId) {
      showError(t('common.selectTargetDepartment'));
      return;
    }
    if (!validateForm()) {
      showError(t('common.required'));
      return;
    }

    setLoading(true);
    try {
      const serviceData = {
        name: formData.name,
        description: formData.description,
        strategic_goal: parseInt(formData.strategicGoal),
        owning_units: formData.owningUnits.map(Number),
        department_id: departmentId || undefined,
        service_fees: formData.serviceFees,
        requirements_conditions: formData.requirementsConditions,
        steps_to_obtain: formData.stepsToObtain,
        delivery_channels: formData.deliveryChannels,
        target_customers: formData.targetCustomers,
        service_limitations: formData.serviceLimitations,
        service_hours: formData.serviceHours,
        additional_info: formData.additionalInfo,
      };

      let savedServiceId: number;
      if (isEditMode && serviceId) {
        // Update existing service
        await servicesApi.update(serviceId, serviceData);
        savedServiceId = serviceId;
      } else {
        // Create new service and capture the ID
        const newService = await servicesApi.create(serviceData);
        savedServiceId = newService.id;
      }

      // Delete removed KPIs from backend
      let kpiDeleteFailed = 0;
      if (deletedKpiIds.length > 0) {
        for (const kpiId of deletedKpiIds) {
          try {
            await kpisApi.delete(kpiId);
          } catch (deleteError: any) {
            kpiDeleteFailed++;
          }
        }
      }
      if (kpiDeleteFailed > 0) {
        showError(t('kpis.deleteFailed'));
      }

      // Handle KPIs - update existing ones and create new ones
      // Only process KPIs that have a name (required field)
      const validKpis = kpis.filter(kpi => kpi.name && kpi.name.trim());

      const kpiErrors: string[] = [];
      for (const kpi of validKpis) {
        const kpiData = {
          name: kpi.name,
          description: kpi.description || '',
          strategic_goal: parseInt(formData.strategicGoal),
          kpi_type: kpi.kpiType || 'operational',
          unit: kpi.unit || '%',
          target_value: (parseFloat(kpi.targetValue) || 0).toFixed(2),
          frequency: kpi.frequency || 'monthly',
          polarity: kpi.polarity || 'positive',
          weight: kpi.weight || '100',
          weight_from_source: kpi.weight || '100',
          measurement_start_date: kpi.startDate || null,
          measurement_formula: kpi.formula || '',
          data_source: kpi.dataSource || '',
          owner: kpi.dataSource || '',
          source_service: savedServiceId,
          source_type: 'service',
        };

        if (kpi.isExisting && kpi.apiId) {
          try {
            await kpisApi.update(kpi.apiId, {
              ...kpiData,
              ...buildKpiTargetPayload(kpi.target, {
                unit: kpi.unit,
                frequency: kpi.frequency,
                startDate: kpi.startDate,
              }),
            });
          } catch (updateError: any) {
            const errMsg = extractKpiErrorMessage(updateError?.response?.data);
            kpiErrors.push(t('kpis.kpiUpdateError', { name: kpi.name, error: errMsg }));
            console.warn(`KPI ${kpi.apiId} update failed:`, updateError?.response?.data);
          }
        } else {
          try {
            await kpisApi.create({
              ...kpiData,
              ...buildKpiTargetPayload(kpi.target, {
                unit: kpi.unit,
                frequency: kpi.frequency,
                startDate: kpi.startDate,
              }),
            });
          } catch (createError: any) {
            const errMsg = extractKpiErrorMessage(createError?.response?.data);
            kpiErrors.push(t('kpis.kpiSaveError', { name: kpi.name, error: errMsg }));
            console.warn(`KPI create failed:`, createError?.response?.data);
          }
        }
      }

      if (kpiErrors.length > 0) {
        // A KPI save failed — surface it and STOP. Do NOT show success or
        // navigate away: that masked the failure and silently discarded the
        // user's KPI input (entered values "disappeared"). Keep them on the
        // form with their data so they can fix and retry.
        showError(kpiErrors.join('\n'));
        return;
      }

      showSuccess(isEditMode ? t('services.updateSuccess') : t('services.createSuccess'));
      // Go to the saved service's detail page instead of goBack() (which can
      // land on the dashboard when the stack is shallow).
      if (savedServiceId) {
        navigation.navigate('ServiceDetail', { id: savedServiceId });
      } else {
        navigation.goBack();
      }
    } catch (error: any) {
      console.error('Error saving service:', error);
      showError(error.response?.data?.detail || (isEditMode ? t('services.updateFailed') : t('services.createFailed')));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const { requestCancel, confirmVisible, confirmDiscard, dismissConfirm } = useCancelConfirm({
    snapshot: { formData, kpis },
    ready: !initialLoading,
    onExit: handleCancel,
  });

  const updateField = (field: string, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Show loading while fetching service data
  if (initialLoading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView ref={scrollViewRef} style={styles.scrollView} contentContainerStyle={styles.content}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('services.title'), route: 'Services' },
            { label: isEditMode ? t('services.editService') : t('services.addService') },
          ]}
        />

        {/* Main Card Container */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder, overflow: 'visible', zIndex: 10 }]}>
          {/* Page Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>{isEditMode ? t('services.editService') : t('services.addService')}</Text>

          {/* Basic Information Section */}
          <View style={[styles.section, { zIndex: 40, overflow: 'visible', backgroundColor: themeColors.section, borderColor: themeColors.inputBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('common.basicInfo')}</Text>

            {/* Target department: only for a multi-department user creating a service.
                An existing service stays in its own department. */}
            {!isEditMode && (
              <DepartmentPicker departments={departments} value={departmentId} onChange={setDepartmentId} />
            )}

            {/* Service Name */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('services.serviceName')}</Text>
              </View>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.name ? styles.inputError : undefined]}
                value={formData.name}
                onChangeText={(v) => updateField('name', v)}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
              />
              {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
            </View>

            {/* Service Description */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>{t('services.serviceDescription')}</Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.description}
                onChangeText={(v) => updateField('description', v)}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Strategic Goal & Org Unit Row */}
            <View style={[styles.row, { zIndex: 100, overflow: 'visible' }]}>
              <View style={[styles.fieldHalf, { zIndex: 1 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label, marginBottom: 8 }]}>{t('initiatives.implementationResponsibility')}</Text>
                </View>
                <MultiSelectDropdown
                  values={formData.owningUnits}
                  options={units}
                  placeholder={t('common.selectResponsible')}
                  onChange={(vals) => updateField('owningUnits', vals)}
                />
              </View>
              <View style={[styles.fieldHalf, { zIndex: 2 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>{t('goals.strategicGoal')}</Text>
                </View>
                <Dropdown
                  value={formData.strategicGoal}
                  options={goals}
                  placeholder={awaitingDepartment ? t('common.selectDepartmentFirst') : t('goals.selectStrategicGoal')}
                  disabled={awaitingDepartment}
                  onChange={(v) => updateField('strategicGoal', v)}
                />
                {errors.strategicGoal && <Text style={styles.errorText}>{errors.strategicGoal}</Text>}
              </View>
            </View>
          </View>

          {/* Details Section (التفاصيل) */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.inputBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('services.details')}</Text>

            {/* Service Fees */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('services.serviceFees')}</Text>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.serviceFees}
                onChangeText={(v) => updateField('serviceFees', v)}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Requirements & Conditions */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('services.requirementsConditions')}</Text>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.requirementsConditions}
                onChangeText={(v) => updateField('requirementsConditions', v)}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Steps to Obtain */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('services.stepsToObtain')}</Text>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.stepsToObtain}
                onChangeText={(v) => updateField('stepsToObtain', v)}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Delivery Channels */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('services.deliveryChannels')}</Text>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.deliveryChannels}
                onChangeText={(v) => updateField('deliveryChannels', v)}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Target Customers */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('services.targetCustomers')}</Text>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.targetCustomers}
                onChangeText={(v) => updateField('targetCustomers', v)}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Service Limitations */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('services.serviceLimitations')}</Text>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.serviceLimitations}
                onChangeText={(v) => updateField('serviceLimitations', v)}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Service Hours */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('services.serviceHours')}</Text>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.serviceHours}
                onChangeText={(v) => updateField('serviceHours', v)}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>

            {/* Additional Info */}
            <View style={styles.field}>
              <Text style={[styles.label, { color: themeColors.label }]}>{t('services.additionalInfo')}</Text>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.additionalInfo}
                onChangeText={(v) => updateField('additionalInfo', v)}
                placeholderTextColor={themeColors.placeholder}
                textAlign="right"
                multiline
                numberOfLines={3}
              />
            </View>
          </View>

          {/* Performance Indicators Section */}
          <View
            ref={kpiSectionRef}
            style={[styles.kpisSection, { zIndex: 30, backgroundColor: themeColors.section }]}
            onLayout={(event) => {
              kpiSectionY.current = event.nativeEvent.layout.y;
            }}
          >
            <View style={styles.kpisSectionHeader}>
              <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('services.linkedKPIs')}</Text>
            </View>

            {/* Loading indicator for existing KPIs */}
            {isEditMode && existingKpisLoading && (
              <View style={styles.existingKpisLoadingContainer}>
                <ActivityIndicator size="small" color={COLORS.primary} />
                <Text style={styles.existingKpisLoadingText}>{t('kpis.loadingKPIs')}</Text>
              </View>
            )}

            {/* KPI Cards (both existing and new) */}
            {kpis.map((kpi, index) => (
              <KPICard
                key={kpi.id}
                kpi={kpi}
                index={index}
                onUpdate={updateKPI}
                onDelete={deleteKPI}
                onToggleExpand={toggleKPIExpand}
              />
            ))}

            {/* Add KPI Button */}
            <TouchableOpacity style={styles.addKpiButton} onPress={addKPI}>
              <Text style={styles.addKpiButtonText}>{t('kpis.addPerformanceIndicator')}</Text>
              <PlusIcon size={16} color={tokens.colors.semantic.general.foreground} />
            </TouchableOpacity>
          </View>

          {/* Action Buttons */}
          <View style={styles.actionButtons}>
            <Button
              variant="primary"
              onPress={handleSave}
              loading={loading}
            >
              {t('common.save')}
            </Button>
            <Button
              variant="secondary"
              onPress={requestCancel}
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
          </View>
        </View>
      </ScrollView>
      <KPIDeleteConfirmModal
        visible={kpiPendingDelete !== null}
        kpiId={kpiPendingDelete?.backendId ?? null}
        kpiName={kpiPendingDelete?.name}
        onConfirm={confirmDeleteKPI}
        onCancel={() => setKpiPendingDelete(null)}
      />
      <CancelConfirmModal
        visible={confirmVisible}
        onConfirm={confirmDiscard}
        onClose={dismissConfirm}
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: tokens.spacing.absolute["6"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
  // Main Card
  mainCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    overflow: 'visible',
  },
  // Page Title
  pageTitle: {
    fontSize: tokens.typography.paragraph["paragraph subheading font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["6"],
  },
  // Section
  section: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  // KPIs Section
  kpisSection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["4"],
    gap: tokens.spacing.absolute["3"],
    overflow: 'visible',
  },
  kpisSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: tokens.spacing.absolute["3"],
  },
  // KPI Card
  kpiCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    marginBottom: tokens.spacing.absolute["3"],
    overflow: 'visible',
  },
  kpiCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["6"],
  },
  kpiHeaderButton: {
    width: 40,
    height: 40,
    borderRadius: tokens.radii.absolute["radius-4"],
    backgroundColor: tokens.colors.raw.slate["100"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiCardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
    flex: 1,
    justifyContent: 'flex-end',
  },
  kpiCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    flex: 1,
    textAlign: getTextAlign(isRTL),
  },
  kpiIndexBadge: {
    width: 40,
    height: 40,
    borderRadius: tokens.radii.absolute["radius-4"],
    backgroundColor: tokens.colors.raw.slate["200"],
    justifyContent: 'center',
    alignItems: 'center',
  },
  kpiIndexText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
  },
  kpiCardContent: {
    padding: tokens.spacing.absolute["3"],
    gap: tokens.spacing.absolute["6"],
    overflow: 'visible',
  },
  kpiSubsection: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["6"],
    gap: tokens.spacing.absolute["6"],
    overflow: 'visible',
  },
  kpiSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
  },
  // Add KPI Button
  addKpiButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["2"],
    minHeight: 36,
    paddingVertical: tokens.spacing.absolute["2"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    backgroundColor: tokens.colors.raw.white,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  addKpiButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Field styles
  field: {
    marginBottom: tokens.spacing.absolute["4"],
  },
  fieldSmall: {
    width: '30%',
  },
  fieldLarge: {
    flex: 1,
  },
  fieldHalf: {
    flex: 1,
    overflow: 'visible',
  },
  row: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["4"],
    marginBottom: tokens.spacing.absolute["4"],
    overflow: 'visible',
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1"],
    marginBottom: tokens.spacing.absolute["2"],
  },
  label: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  requiredStar: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    color: tokens.colors.raw.red["500"],
  },
  // Input styles
  input: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  inputError: {
    borderColor: tokens.colors.raw.red["500"],
  },
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
    textAlign: getTextAlign(isRTL),
    marginTop: tokens.spacing.absolute["1"],
  },
  // Weight Input
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  weightInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
  },
  weightInput: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    paddingVertical: tokens.spacing.absolute["2_5"],
  },
  percentSymbolInside: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    marginEnd: tokens.spacing.absolute["1"],
  },
  // Date Input
  dateInput: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 7.5,
    minHeight: 36,
    gap: tokens.spacing.absolute["2"],
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
    elevation: 1,
  },
  dateInputText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  setWeightButton: {
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
  },
  setWeightButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  // Dropdown styles
  dropdownContainer: {
    position: 'relative',
    zIndex: 1000,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: tokens.colors.raw.white,
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: tokens.spacing.absolute["2_5"],
    gap: tokens.spacing.absolute["2"],
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  dropdownItem: {
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
  },
  dropdownItemText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    direction: 'ltr' as any,
    justifyContent: 'flex-start',
    gap: tokens.spacing.absolute["3"],
    marginTop: tokens.spacing.absolute["2"],
  },
  // Existing KPIs Section Styles
  existingKpisLoadingContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["6"],
  },
  existingKpisLoadingText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
  },
  emptyKpisContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: tokens.spacing.absolute["8"],
    gap: tokens.spacing.absolute["3"],
  },
  emptyKpisText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["400"],
    textAlign: 'center',
  },
});

const styles = getStyles(true);
